import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Wallet, Calendar, IndianRupee, Plus, Minus } from "lucide-react";
import { toast } from "sonner";
import api, { fmtINR, fmtDate } from "@/lib/api";
import TenantBrandingEditor from "./TenantBrandingEditor";

export default function SuperTenantDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [extendDays, setExtendDays] = useState(30);
  const [payAmt, setPayAmt] = useState("");
  const [adjAmt, setAdjAmt] = useState("");
  const [adjType, setAdjType] = useState("credit");
  const [adjRemarks, setAdjRemarks] = useState("");

  const load = async () => {
    const t = await api.get("/super-admin/tenants");
    setTenants(t.data);
    const w = await api.get(`/super-admin/tenants/${id}/wallet`);
    setWallet(w.data);
  };
  useEffect(() => { load(); }, [id]);

  const tenant = tenants.find((t) => t.id === id);
  if (!tenant) return <div className="p-6 text-center text-slate-500">Loading...</div>;

  const extend = async () => {
    await api.post(`/super-admin/tenants/${id}/extend?days=${extendDays}`);
    toast.success(`Extended by ${extendDays} days`); load();
  };
  const markPaid = async () => {
    if (!payAmt) return;
    await api.post(`/super-admin/tenants/${id}/payment`, { amount: parseFloat(payAmt), months: 1, remarks: "Offline" });
    toast.success("Payment recorded"); setPayAmt(""); load();
  };
  const adjustWallet = async () => {
    if (!adjAmt) return;
    try {
      await api.post(`/super-admin/tenants/${id}/wallet/adjust`, {
        amount: parseFloat(adjAmt), type: adjType, remarks: adjRemarks || "Manual",
      });
      toast.success("Wallet adjusted");
      setAdjAmt(""); setAdjRemarks(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="super-tenant-detail">
      <button data-testid="back-to-tenants" onClick={() => nav(-1)} className="inline-flex items-center gap-1 text-sm text-slate-500"><ArrowLeft className="w-4 h-4" />Back</button>

      <div className="mt-3 bg-white border border-slate-100 rounded-2xl p-6 card-shadow flex items-center gap-4">
        <div className="w-14 h-14 rounded-2xl grid place-items-center text-white overflow-hidden" style={{ background: tenant.primary_color }}>
          {tenant.logo_url ? (
            <img src={tenant.logo_url} alt={tenant.brand_name} className="w-full h-full object-cover" />
          ) : (
            <Wallet className="w-7 h-7" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-extrabold tracking-tight text-xl truncate">{tenant.brand_name || tenant.name}</div>
          <div className="text-xs text-slate-500">+91 {tenant.phone} · {tenant.status}{tenant.slug ? ` · /?tenant=${tenant.slug}` : ""}</div>
        </div>
      </div>

      <div className="mt-5">
        <TenantBrandingEditor tenant={tenant} onSaved={load} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-5">
        <div className="bg-white border border-slate-100 rounded-2xl p-5 card-shadow">
          <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-400">Subscription</div>
          <div className="text-xl font-extrabold mt-1">{fmtINR(tenant.subscription_amount)}</div>
          <div className="text-xs text-slate-500 mt-1">Renewal: {fmtINR(tenant.renewal_amount)}</div>
        </div>
        <div className="bg-white border border-slate-100 rounded-2xl p-5 card-shadow">
          <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-400">Subscription Period</div>
          <div className="text-sm font-bold mt-1">{fmtDate(tenant.subscription_start)} → {fmtDate(tenant.subscription_end)}</div>
          <div className="text-xs text-slate-500 mt-1 capitalize">Status: {tenant.payment_status}</div>
        </div>
        <div className="bg-white border border-slate-100 rounded-2xl p-5 card-shadow">
          <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-400">Dues</div>
          <div className="text-xl font-extrabold mt-1 text-rose-600">{fmtINR(tenant.due_amount)}</div>
          <div className="text-xs text-slate-500 mt-1">Paid: {fmtINR(tenant.paid_amount)}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
        <div className="bg-white border border-slate-100 rounded-2xl p-5 card-shadow">
          <div className="font-extrabold">Subscription Actions</div>
          <div className="mt-3 flex items-center gap-2">
            <input data-testid="extend-days" type="number" value={extendDays} onChange={(e) => setExtendDays(e.target.value)} className="w-24 border-2 border-slate-200 rounded-xl px-3 py-2 outline-none" />
            <span className="text-sm text-slate-500">days</span>
            <button data-testid="extend-btn" onClick={extend} className="ml-auto bg-tenant-primary text-white px-3 py-2 rounded-xl font-bold text-xs inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />Extend</button>
          </div>
          <div className="mt-4 flex items-center gap-2">
            <div className="flex items-center gap-1 border-2 border-slate-200 rounded-xl px-3 py-2 flex-1">
              <IndianRupee className="w-4 h-4 text-slate-400" />
              <input data-testid="pay-amount" value={payAmt} onChange={(e) => setPayAmt(e.target.value.replace(/[^\d.]/g, ""))} placeholder="Amount" className="outline-none flex-1" />
            </div>
            <button data-testid="mark-paid-btn" onClick={markPaid} className="bg-emerald-600 text-white px-4 py-2 rounded-xl font-bold text-xs">Mark Paid</button>
          </div>
        </div>

        <div className="bg-white border border-slate-100 rounded-2xl p-5 card-shadow">
          <div className="font-extrabold">OTP Wallet</div>
          <div className="text-2xl font-extrabold tracking-tight mt-2" data-testid="otp-wallet-balance">{fmtINR(wallet?.wallet?.balance || 0)}</div>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <button onClick={() => setAdjType("credit")} className={`py-2 rounded-xl text-xs font-bold ${adjType === "credit" ? "bg-emerald-600 text-white" : "bg-slate-100"}`}>Credit</button>
            <button onClick={() => setAdjType("debit")} className={`py-2 rounded-xl text-xs font-bold ${adjType === "debit" ? "bg-rose-600 text-white" : "bg-slate-100"}`}>Debit</button>
          </div>
          <input data-testid="wallet-adj-amount" value={adjAmt} onChange={(e) => setAdjAmt(e.target.value.replace(/[^\d.]/g, ""))} placeholder="Amount" className="mt-2 w-full border-2 border-slate-200 rounded-xl px-3 py-2 outline-none" />
          <input data-testid="wallet-adj-remarks" value={adjRemarks} onChange={(e) => setAdjRemarks(e.target.value)} placeholder="Remarks" className="mt-2 w-full border-2 border-slate-200 rounded-xl px-3 py-2 outline-none" />
          <button data-testid="confirm-wallet-adj" onClick={adjustWallet} className="w-full mt-3 bg-slate-900 text-white py-2.5 rounded-xl font-bold text-xs inline-flex items-center justify-center gap-1">
            {adjType === "credit" ? <Plus className="w-3.5 h-3.5" /> : <Minus className="w-3.5 h-3.5" />}
            Apply {adjType}
          </button>
        </div>
      </div>

      <div className="bg-white border border-slate-100 rounded-2xl p-5 card-shadow mt-5">
        <div className="font-extrabold mb-3">Wallet Ledger</div>
        <div className="max-h-96 overflow-y-auto divide-y">
          {wallet?.ledger?.length ? wallet.ledger.map((l) => (
            <div key={l.id} className="py-2.5 flex items-center justify-between">
              <div>
                <div className="font-bold text-sm">{l.remarks}</div>
                <div className="text-[11px] text-slate-400">{fmtDate(l.created_at)} · {l.reference}</div>
              </div>
              <div className={`font-extrabold text-sm ${l.type === "credit" ? "text-emerald-600" : "text-rose-500"}`}>{l.type === "credit" ? "+" : "-"}{fmtINR(l.amount)}</div>
            </div>
          )) : <div className="text-center text-slate-400 py-6">No ledger entries.</div>}
        </div>
      </div>
    </div>
  );
}
